import {
  booleanAttribute,
  Component,
  computed,
  input,
  signal,
  ChangeDetectionStrategy,
  inject,
  afterNextRender,
  DestroyRef,
  ElementRef,
  NgZone,
} from '@angular/core';
import { DARK_THEME_CONFIG } from './config/dark-theme.config';
import { LIGHT_THEME_CONFIG } from './config/light-theme.config';
import { SIZE_CONFIG } from './config/size.config';
import { ButtonMode, ButtonSize, ButtonTheme } from './button.models';

@Component({
  selector: 'mib-button',
  templateUrl: './button.component.html',
  host: { '[class]': 'hostClasses()' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MibButtonComponent {
  readonly elementRef = inject(ElementRef);
  private readonly ngZone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Colour transitions are only enabled once the button has been painted with its themed colours.
   *
   * When a button is created inside content that is inserted into the DOM before Angular applies its
   * bindings (CDK overlays: dialogs, modals, menus…), the browser can paint the native button once
   * with its default background. Enabling `transition-colors` on that very first frame would turn
   * that one-frame gap into a 150ms fade towards the themed colour, which reads as a blink when the
   * overlay opens. Waiting for one painted frame keeps the mount instantaneous while preserving the
   * hover/active transitions.
   */
  private readonly transitionsEnabled = signal(false);

  constructor() {
    afterNextRender(() => this.enableTransitionsAfterFirstPaint());
  }

  /**
   * Flips {@link transitionsEnabled} once the current frame has actually been painted.
   *
   * The first `requestAnimationFrame` runs before the pending style recalculation and paint, so a
   * second one is needed to guarantee the themed colours have been rendered before the transition
   * property is added.
   */
  private enableTransitionsAfterFirstPaint(): void {
    this.ngZone.runOutsideAngular(() => {
      let innerFrameId = 0;
      const outerFrameId = requestAnimationFrame(() => {
        innerFrameId = requestAnimationFrame(() => this.transitionsEnabled.set(true));
      });

      this.destroyRef.onDestroy(() => {
        cancelAnimationFrame(outerFrameId);
        cancelAnimationFrame(innerFrameId);
      });
    });
  }

  readonly type = input<'button' | 'submit' | 'reset'>('button');
  /** Id of the form this button controls, for submit buttons rendered outside of their `<form>`. */
  readonly form = input<string | null>(null);
  readonly disabled = input(false, { transform: booleanAttribute });
  readonly active = input(false, { transform: booleanAttribute });
  readonly iconOnly = input(false, { transform: booleanAttribute });
  readonly darkMode = input(false, { transform: booleanAttribute });
  readonly alignLeft = input(false, { transform: booleanAttribute });
  readonly withoutPadding = input(false, { transform: booleanAttribute });
  readonly heightClass = input<string>();

  readonly size = input<ButtonSize>('large');
  readonly theme = input<ButtonTheme>('primary');
  readonly mode = input<ButtonMode>('filled');
  readonly shape = input<string>('rounded-mib');
  readonly tabIndex = input<number | null>(null);
  readonly ariaLabel = input<string | null>(null, { alias: 'aria-label' });

  readonly effectiveShape = computed(() => this.shape() || 'rounded-mib');

  readonly hostClasses = computed(() =>
    [
      'block',
      'mib-button-default-width',
      'has-[:focus-visible]:outline-2',
      'outline-offset-2',
      this.darkMode() ? 'outline-btn-dark-primary-bg' : 'outline-mib-primary',
      this.effectiveShape(),
    ].join(' '),
  );

  readonly classes = computed(() => {
    const classes: string[] = [
      // Base styles
      'flex',
      'items-center',
      'box-border',
      'select-none',
      'border',
      'focus:outline-none', // remove default outline focus ring on button as we manage the focus ring on the host
    ];

    // Colour transitions, enabled only after the first paint (see transitionsEnabled)
    if (this.transitionsEnabled()) {
      classes.push('transition-colors');
    }

    // Alignment
    if (!this.alignLeft()) {
      classes.push('justify-center');
    }

    // Shape
    classes.push(this.effectiveShape());

    // Cursor
    classes.push(this.disabled() ? 'cursor-default' : 'cursor-pointer');

    // Size classes
    classes.push(...this.getSizeClasses());

    // Theme classes
    classes.push(...this.getThemeClasses());

    return classes.join(' ');
  });

  /**
   * Returns the size-related CSS classes based on button size and layout configuration
   */
  private getSizeClasses(): string[] {
    const sizeConfig = SIZE_CONFIG[this.size()];
    const classes: string[] = [];

    // Width
    if (this.iconOnly()) {
      classes.push(sizeConfig.width.iconOnly);
    } else {
      classes.push(sizeConfig.width.regular);
      // Padding
      if (!this.withoutPadding()) {
        classes.push(this.alignLeft() ? sizeConfig.padding.alignLeft : sizeConfig.padding.regular);
      }
    }

    // Height, text size, and gap
    classes.push(sizeConfig.text, sizeConfig.gap);
    const externalHeightClass = this.heightClass();
    if (!externalHeightClass) {
      classes.push(sizeConfig.height);
    } else {
      classes.push(externalHeightClass);
    }

    return classes;
  }

  /**
   * Returns the theme-related CSS classes based on theme, mode, dark mode, and state
   */
  private getThemeClasses(): string[] {
    // Select the appropriate theme configuration
    const themeConfig = this.darkMode() ? DARK_THEME_CONFIG : LIGHT_THEME_CONFIG;

    // Get the specific configuration for the current theme and mode
    const modeConfig = themeConfig[this.theme()][this.mode()];

    // Determine which state configuration to use
    let stateConfig;
    if (this.disabled()) {
      stateConfig = modeConfig.disabled;
    } else if (this.active()) {
      stateConfig = modeConfig.active;
    } else {
      stateConfig = modeConfig.enabled;
    }

    // Return the pre-configured class strings
    return [stateConfig.bg, stateConfig.text, stateConfig.border];
  }
}
